import {
  Link,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Stack,
  Typography,
  capitalize,
  useTheme,
} from "@mui/material";
import { useCallback, useMemo, useRef } from "react";
import { CellContext, ColumnDef, SortingState } from "@tanstack/react-table";
import { Business, MenuBook, Route, ViewModule } from "@mui/icons-material";
import pluralize from "pluralize";
import {
  SELECTED_DATE_PARAM_KEY,
  useDateSearchParamOrFallbackToToday,
} from "../../../hooks/useDateSearchParamOrFallbackToToday";
import { useSelectedServiceCenter } from "../../../hooks/useSelectedServiceCenter";
import SectionTitle from "../prePlanning/tables/SectionTitle";
import { dateToInt } from "../../../utils/dateTimeHelper";
import { useShipmentDetailSearch } from "../../../hooks/react-query/preplanning/useShipmentDetails";
import { shipmentColumns } from "../../../components/shared/table/shipmentsColumnDef";
import Table, { TableRef } from "../../../components/shared/table/Table";
import { RowSelectionMode } from "../../../components/shared/table/Selectable.types";
import { useSearchTermParam } from "../../../hooks/useSearchTermParam";
import { useIsSelectedDateTodayOrInTheFutureForCurrentSic } from "../../../hooks/useIsSelectedDateTodayOrInTheFutureForCurrentSic";
import { Totalizer } from "../../../components/shared/totalizer";
import {
  filterShipmentData,
  getShipmentTableFilters,
} from "../../../constants/filters/shipmentTableFilters";
import { Shipment } from "../../../services/prePlanningService.types";
import { contextMenuActions } from "../prePlanning/tables/contextMenuActions";
import { TableShipment } from "../../../components/shared/old-table/ShipmentsTableBase";
import { useIsFeatureFlagEnabled } from "../../../featureFlags/useIsFeatureFlagEnabled";
import { InboundView } from "../../../types/inbound/inboundView.type";
import { useFilterSearchParams } from "../../../hooks/useFilterSearchParams";
import { keepSpecificParams } from "../../../utils/searchParamHelper";
import { UNPLANNED_SELECTED_DATE } from "../../../hooks/useUnplannedDatetimeSearchParam";
import { useTableSelectionParam } from "../../../hooks/useTableSelectionParam";
import { unifyShipmentContext } from "../../../utils/unifyShipmentContext";
import { DragMultipleShipments } from "../../../components/shared/DragMultipleShipments";
import { useDebouncedSearch } from "../../../hooks/useDebouncedSearch";
import type { AssignShipmentType } from "../../../types/assignShipment.type";
import { SearchBox } from "../../../components/shared/SearchBox";

import { useGetAppointmentBucketData } from "../../../hooks/react-query/preplanning/useGetAppointmentBucket";

import { QuickPlannedFilter } from "../unplannedFreight/shared/QuickPlannedFilter";
import { useInboundAppointmentSummary } from "../../../hooks/react-query/useInboundAppointmentSumary";
import {
  TRAILER_NAME_PARAM,
  TRAILER_TITLE_PARAM,
} from "../../../components/shared/table/TableLink";
import { CopyLinkIconButton } from "../inboundPlanning/tables/CopyLinkIconButton";
import Filters from "./Filter/Filters";
import { SummaryContext, useActionsMenuContext } from "./ActionsMenuContext";
import InboundActionBar from "./InboundActionBar";
import TableActionBar, {
  TableSection,
  tableSectionSchema,
} from "./TableActionBar";
import { formatTrailerTitle } from "./utils";
import ShipmentsPlannedProgressBarWithLabel from "./ShipmentsPlannedProgressBarWithLabel";

const VIEW_TABLE_OPTIONS = [
  {
    tab: "route",
    icon: <Route />,
  },
  {
    tab: "section",
    icon: <Business />,
  },
  {
    tab: "trap",
    icon: <ViewModule />,
  },
  {
    tab: "bucket",
    icon: <MenuBook />,
  },
] as const;

type ShipmentTableProps = {
  view: InboundView;
  rowSelectionMode: RowSelectionMode;
  showPresetRouteColumn?: boolean;
  isDraggable?: boolean;
  isSortable?: boolean;
  haveContextMenu?: boolean;
  defaultSort?: SortingState;
};

const ShipmentsTable = ({
  /** inbound page */
  view,
  /** define the mode of selection */
  rowSelectionMode = "unselectable",
  /** default sort array */
  defaultSort = [],
  isDraggable = false,
  isSortable = false,
  haveContextMenu,
  showPresetRouteColumn = false,
}: ShipmentTableProps) => {
  const { selection, setSelection } = useTableSelectionParam();

  const isMexicoTooltipEnabled = useIsFeatureFlagEnabled(
    "inbound-3879-mexico-tooltip-client"
  );

  const isEditingEnabled = useIsSelectedDateTodayOrInTheFutureForCurrentSic();
  const theme = useTheme();
  const { id = "" } = useParams();
  const { pathname, search } = useLocation();
  const [searchParams] = useSearchParams();
  const tableRef = useRef<TableRef>(null);
  const [serviceCenter] = useSelectedServiceCenter();
  const [selectedDate] = useDateSearchParamOrFallbackToToday();
  const planDate = dateToInt(selectedDate);
  const { openDialog } = useActionsMenuContext();
  const [searchTerm, setSearchTerm] = useSearchTermParam();
  const debouncedSearch = useDebouncedSearch(searchTerm);
  const [filters] = useFilterSearchParams({
    presetRouteName: [],
    trailerNumber: [],
    serviceDueDate: [],
    appointmentDate: [],
    shipperName: [],
    consigneeName: [],
    consigneeAddress: [],
    consigneeCity: [],
    consigneeZip: [],
    destinationSicCode: [],
    status: [],
    tags: [],
    pieces: [],
    pallets: [],
    weight: [],
    eta: [],
    isPlannedFilter: [],
  });

  const { trailerName, trailerTitle, shipmentIds } = useMemo(
    function getSearchParams() {
      const searchParams = new URLSearchParams(search);
      const trailerTitle = searchParams.get(TRAILER_TITLE_PARAM);
      const trailerName = searchParams.get(TRAILER_NAME_PARAM);
      const shipmentIdsParam = searchParams.get("shipmentIds");

      const shipmentIds = shipmentIdsParam
        ? shipmentIdsParam.split(",").map((id) => Number(id))
        : [];
      return {
        trailerName,
        trailerTitle,
        shipmentIds,
      } as const;
    },
    [search]
  );

  const { data: shipments = [], isLoading } = useShipmentDetailSearch({
    search: {
      shipmentIds,
      searchTerm: debouncedSearch,
      sicId: serviceCenter.id,
      planDate,
    },
  });

  const { data: appointments = [] } = useGetAppointmentBucketData(
    serviceCenter.id,
    debouncedSearch
  );

  const summary = useInboundAppointmentSummary(shipments);

  const planned =
    appointments.filter((appointment) => appointment.title === trailerTitle)[0]
      ?.plannedShipmentCount || 0;

  const filteredShipments = filterShipmentData(
    shipments,
    filters,
    serviceCenter.timeZone
  );

  const shipmentFilters = useMemo(
    () =>
      getShipmentTableFilters(
        shipments,
        serviceCenter.timeZone,
        searchParams,
        undefined
      ),
    [searchParams, serviceCenter.timeZone, shipments]
  );

  const pathToMainTable = useMemo(
    function createPathToMainTable() {
      let pathSegments = pathname.split("/").filter(Boolean);
      if (pathSegments.length > 1) {
        pathSegments = pathSegments.slice(0, pathSegments.length - 2);
        return "/" + pathSegments.join("/");
      }
    },
    [pathname]
  );

  const mainTableSection: TableSection = useMemo(() => {
    const fragments = pathToMainTable?.split("/") || "";
    const sectionValidation = tableSectionSchema.safeParse(
      pluralize.singular(fragments[fragments.length - 1])
    );
    if (!sectionValidation.success) {
      throw new Error("Table section not defined");
    }
    return sectionValidation.data;
  }, [pathToMainTable]);

  function getType(): AssignShipmentType {
    switch (view) {
      case "appointments":
        return "bucket";
      case "sections":
        return "section";
      case "routes":
        return "route";
      case "traps":
        return "trap";
      default:
        return "bucket";
    }
  }

  const type = getType();

  const selectionToContext = useCallback(
    (shipment?: Shipment): SummaryContext[] => {
      if (shipment) {
        return [
          {
            id: id || "",
            shipmentIds: [shipment.id],
            type,
          },
        ];
      }
      return unifyShipmentContext(
        selection.map((shipmentId) => {
          const shipment = filteredShipments.find(
            (shipment) => shipment.id.toString() === shipmentId
          );
          return {
            id,
            shipmentIds: shipment ? [shipment.id] : [],
            type,
          };
        })
      );
    },
    [filteredShipments, id, selection, type]
  );

  const columns = useMemo(
    () =>
      shipmentColumns({
        timeZone: serviceCenter.timeZone,
        selectedDate,
        showPresetRouteColumn,
        isMexicoTooltipEnabled,
      }),
    [
      selectedDate,
      serviceCenter.timeZone,
      showPresetRouteColumn,
      isMexicoTooltipEnabled,
    ]
  );

  const dragCell = useCallback(
    ({ cell, row }: CellContext<TableShipment, unknown>) =>
      isDraggable && <DragMultipleShipments row={row} cell={cell} />,
    [isDraggable]
  );

  const commonProps = useMemo(() => {
    const preColumns: ColumnDef<TableShipment, unknown>[] = [
      {
        id: "drag",
        header: "",
        enableSorting: false,
        cell: dragCell,
      },
    ];

    return {
      ref: tableRef,
      caption: `${id} shipments`,
      isLoading,
      columns,
      preColumns: isDraggable ? preColumns : [],
      data: filteredShipments,
      defaultSort,
    };
  }, [
    columns,
    defaultSort,
    filteredShipments,
    id,
    isDraggable,
    isLoading,
    dragCell,
  ]);

  const tableTitle = capitalize(formatTrailerTitle(trailerName, trailerTitle));

  return (
    <>
      <Breadcrumbs sx={{ marginBottom: 2 }} aria-label="shipment-breadcrumbs">
        <Link
          to={{
            pathname: pathToMainTable ?? "/",
            search: keepSpecificParams(new URLSearchParams(search), [
              SELECTED_DATE_PARAM_KEY,
              UNPLANNED_SELECTED_DATE,
            ]).toString(),
          }}
        >
          {capitalize(view)}
        </Link>
        <Box display="flex" alignItems="center" gap={1} color="text.primary">
          {VIEW_TABLE_OPTIONS.map(
            (view) =>
              view.tab === mainTableSection && (
                <Box
                  key={view.tab}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                    backgroundColor: theme.palette.grey[200],
                    borderRadius: 1,
                    svg: {
                      fontSize: 18,
                      color: theme.palette.grey[600],
                    },
                  }}
                >
                  {view.icon}
                </Box>
              )
          )}
          <Typography>{tableTitle}</Typography>
        </Box>
      </Breadcrumbs>

      <Stack
        direction="row"
        gap={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <SectionTitle
          page={tableTitle}
          count={shipments.length}
          showToggles={false}
        />
        {view === "appointments" && <CopyLinkIconButton />}
      </Stack>

      <Totalizer.Root data={shipments}>
        <Totalizer.Content name="Bills" property="length" />
        <Totalizer.Content name="Plts" property="pallets" />
        <Totalizer.Content name="Pcs" property="pieces" />
        <Totalizer.Content name="lbs" property="weight" />
      </Totalizer.Root>
      {view === "appointments" && (
        <ShipmentsPlannedProgressBarWithLabel
          current={planned}
          total={shipmentIds.length}
          summary={summary}
        />
      )}
      <TableActionBar
        isShipmentTable
        section="list"
        selectedRows={shipments.filter((shipment) =>
          selection.includes(shipment.id.toString())
        )}
        clearSelection={tableRef.current?.clearSelection}
        actions={
          isEditingEnabled && (
            <InboundActionBar
              selectedRowsContext={selectionToContext()}
              activeTab="shipments"
              clearSelection={tableRef.current?.clearSelection}
              contextMenuActions={contextMenuActions({
                shipmentContext: selectionToContext(),
                section: view,
                openDialog,
                clearSelection: tableRef.current?.clearSelection,
              })}
            />
          )
        }
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Filters
            filters={shipmentFilters}
            hasQuickTypeFilter={view === "appointments"}
            hasFilterOptions={true}
            searchBoxComponent={
              <SearchBox
                searchTerm={searchTerm}
                onValueChange={setSearchTerm}
                placeholder="Search PRO Number, Shipper or Consignee Name"
              />
            }
          />
          <QuickPlannedFilter />
        </Stack>
      </TableActionBar>

      {rowSelectionMode !== "unselectable" && isEditingEnabled ? (
        <Table
          {...commonProps}
          getRowId={(row) => `${row.id}`}
          rowSelectionMode={rowSelectionMode}
          onRowSelection={setSelection}
          isSortable={isSortable}
          droppableTableData={(row) => ({
            index: row.index,
            source: "shipmentTable",
            type,
            id: row.id,
            parentId: id,
          })}
          emptyDroppableTableData={{
            id,
            index: 0,
            source: "shipmentTable",
            type,
            parentId: id,
          }}
          contextMenuActions={
            haveContextMenu
              ? (shipment) =>
                  contextMenuActions({
                    shipmentContext: selectionToContext(shipment),
                    section: view,
                    openDialog,
                    clearSelection: tableRef.current?.clearSelection,
                  })
              : undefined
          }
        />
      ) : (
        <Table {...commonProps} />
      )}
    </>
  );
};

export default ShipmentsTable;
